// Report and block controls for another manager.
//
// Required by App Store Guideline 1.2, which asks every app carrying
// user-generated content for a way to report the content and a way to block
// the person who posted it. The content in question here is a manager's team
// name, their avatar image and their one-line smack talk.
//
// Opened from the manager card on the league standings.
import React, { useState } from "react";
import { supabase } from "../supabase/supabase";
import { invalidateLeague } from "../utils/queryCache";

const CONTENT_TYPES = [
  { value: "team_name", label: "Team name" },
  { value: "smack_talk", label: "Smack talk" },
  { value: "avatar", label: "Avatar image" },
  { value: "other", label: "Something else" },
];

function ReportBlockModal({ member, leagueId, isBlocked, onClose, onChanged }) {
  const [mode, setMode] = useState(null); // null | 'report'
  const [contentType, setContentType] = useState("smack_talk");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const reportedUserId = member?.userId || member?.user_id;
  const displayName = member?.teamName || member?.team_name || "this manager";

  // The snapshot the report carries. A report is reviewed after the fact, and
  // the manager can change any of these the moment they are reported, so the
  // text has to be captured now rather than looked up later.
  const snapshotFor = (type) => {
    if (type === "team_name") return member?.teamName || member?.team_name || null;
    if (type === "smack_talk") return member?.smack_talk || null;
    if (type === "avatar") return member?.teamAvatar || member?.avatar_url || null;
    return null;
  };

  const submitReport = async () => {
    if (!reason.trim()) {
      setError("Please say what is wrong with the content.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");

      const { error: insertError } = await supabase.from("content_reports").insert({
        reporter_id: user.id,
        reported_user_id: reportedUserId,
        league_id: leagueId,
        content_type: contentType,
        content_text: snapshotFor(contentType),
        reason: reason.trim().slice(0, 500),
      });
      if (insertError) throw insertError;

      setDone("Report received. We review reports within 24 hours and remove content that breaks the rules.");
      setMode(null);
    } catch (err) {
      console.error("Error filing report:", err);
      setError("The report could not be sent. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleBlock = async () => {
    setBusy(true);
    setError("");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");

      if (isBlocked) {
        const { error: delError } = await supabase
          .from("user_blocks")
          .delete()
          .eq("blocker_id", user.id)
          .eq("blocked_id", reportedUserId);
        if (delError) throw delError;
        setDone("Manager unblocked. Their team name, avatar and smack talk are visible again.");
      } else {
        const { error: insError } = await supabase
          .from("user_blocks")
          .insert({ blocker_id: user.id, blocked_id: reportedUserId });
        if (insError) throw insError;
        setDone("Manager blocked. You will no longer see their team name, avatar or smack talk. They keep their place in the standings, because removing them would change the league for everyone.");
      }

      // The league query cache is keyed by league and week, so a block does not
      // invalidate it on its own and the standings would keep serving the
      // pre-block copy until it expired.
      invalidateLeague(leagueId);
      onChanged?.();
    } catch (err) {
      console.error("Error updating block:", err);
      setError("That could not be saved. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const buttonBase = {
    width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14,
    fontWeight: 600, border: "none", cursor: busy ? "not-allowed" : "pointer",
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full p-5"
        style={{ maxWidth: 360 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-base font-bold mb-1" style={{ color: "var(--color-text-strong)" }}>
          {displayName}
        </div>
        <p className="text-[13px] mb-4" style={{ color: "var(--color-text-muted)" }}>
          Report content that breaks the league rules, or block this manager so
          you no longer see anything they write.
        </p>

        {done && (
          <div className="rounded-xl px-3 py-2 mb-3" style={{ backgroundColor: "var(--color-success-bg)" }}>
            <span className="text-[13px]" style={{ color: "var(--color-success-deep)" }}>{done}</span>
          </div>
        )}
        {error && (
          <div className="rounded-xl px-3 py-2 mb-3" style={{ backgroundColor: "var(--color-danger-bg)" }}>
            <span className="text-[13px]" style={{ color: "var(--color-danger-text)" }}>{error}</span>
          </div>
        )}

        {mode === "report" ? (
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-[13px] font-semibold block mb-1.5" style={{ color: "var(--color-text-muted)" }}>
                What are you reporting?
              </label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-sm"
                style={{ border: "1.5px solid var(--color-border)", color: "var(--color-text-strong)", backgroundColor: "var(--color-bg-card)" }}
              >
                {CONTENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[13px] font-semibold block mb-1.5" style={{ color: "var(--color-text-muted)" }}>
                What is wrong with it?
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                rows={3}
                maxLength={500}
                className="w-full rounded-lg px-3 py-2 text-sm"
                style={{ border: "1.5px solid var(--color-border)", color: "var(--color-text-strong)", resize: "none" }}
                placeholder="Describe the problem."
              />
            </div>
            <button type="button" onClick={submitReport} disabled={busy}
              style={{ ...buttonBase, backgroundColor: "var(--color-brand)", color: "var(--color-bg-card)" }}>
              {busy ? "Sending..." : "Send report"}
            </button>
            <button type="button" onClick={() => { setMode(null); setError(""); }} disabled={busy}
              style={{ ...buttonBase, backgroundColor: "var(--color-bg-page)", color: "var(--color-text-muted)" }}>
              Back
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            <button type="button" onClick={() => { setMode("report"); setDone(""); }} disabled={busy}
              style={{ ...buttonBase, backgroundColor: "var(--color-bg-page)", color: "var(--color-text-strong)" }}>
              Report content
            </button>
            <button type="button" onClick={toggleBlock} disabled={busy}
              style={{ ...buttonBase, backgroundColor: isBlocked ? "var(--color-bg-page)" : "var(--color-danger-bg)", color: isBlocked ? "var(--color-text-strong)" : "var(--color-danger-text)" }}>
              {isBlocked ? "Unblock manager" : "Block manager"}
            </button>
            <button type="button" onClick={onClose} disabled={busy}
              className="text-sm font-medium text-brand mt-1">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportBlockModal;
